/**
 * DealPriorityBadgePremium - Badge de prioridade com visual premium
 * Cores pastéis suaves e ícone de flag estilo ClickUp
 * 
 * Versão: 1.1.0
 * - Adicionada variante 'inline' para exibição simplificada (ícone + texto)
 */
import React from 'react';
import { Flag } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DealPriority } from '@/hooks/useCrmDeals';

// Props do componente
interface DealPriorityBadgePremiumProps {
  priority: DealPriority;
  className?: string;
  size?: 'xs' | 'sm';
  showLabel?: boolean;
  /** Variante de exibição: 'badge' (padrão com fundo) ou 'inline' (apenas ícone + texto) */
  variant?: 'badge' | 'inline';
}

// Configuração visual premium por prioridade
const priorityConfig: Record<DealPriority, { 
  label: string;
  className: string;
  iconColor: string;
  solidColor: string;        // Cor sólida para variant='inline'
  textColorInline: string;   // Cor do texto para variant='inline'
}> = {
  low: {
    label: 'Baixa',
    className: 'bg-slate-100 text-slate-600 dark:bg-slate-800/50 dark:text-slate-400',
    iconColor: 'text-slate-400 dark:text-slate-500',
    solidColor: '#64748b',
    textColorInline: 'text-slate-600 dark:text-slate-400'
  },
  medium: {
    label: 'Média',
    className: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
    iconColor: 'text-blue-500 dark:text-blue-400',
    solidColor: '#3b82f6',
    textColorInline: 'text-blue-600 dark:text-blue-400'
  },
  high: {
    label: 'Alta',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    iconColor: 'text-amber-500 dark:text-amber-400',
    solidColor: '#f97316',
    textColorInline: 'text-orange-600 dark:text-orange-400'
  },
  urgent: {
    label: 'Urgente',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    iconColor: 'text-red-500 dark:text-red-400',
    solidColor: '#ef4444',
    textColorInline: 'text-red-600 dark:text-red-400'
  }
};

/**
 * Badge premium de prioridade do Deal
 */
export function DealPriorityBadgePremium({ 
  priority, 
  className,
  size = 'xs',
  showLabel = true,
  variant = 'badge'
}: DealPriorityBadgePremiumProps) {
  const config = priorityConfig[priority];

  const iconSize = size === 'xs' ? 'h-2.5 w-2.5' : 'h-3 w-3';
  const textSize = size === 'xs' ? 'text-[10px]' : 'text-xs';

  // Variante inline: apenas ícone preenchido + texto colorido (sem fundo)
  if (variant === 'inline') {
    return (
      <span className={cn('inline-flex items-center gap-1.5', className)}>
        <Flag 
          className={iconSize}
          style={{ color: config.solidColor }}
          fill={config.solidColor}
        />
        {showLabel && (
          <span className={cn('font-medium', textSize, config.textColorInline)}>
            {config.label}
          </span>
        )}
      </span>
    );
  }

  // Variante badge (padrão): com fundo colorido arredondado
  const sizeClasses = size === 'xs' 
    ? 'text-[10px] px-1.5 py-0.5 gap-1' 
    : 'text-xs px-2 py-0.5 gap-1.5';

  return (
    <span 
      className={cn(
        'inline-flex items-center font-medium rounded-full',
        sizeClasses,
        config.className,
        className
      )}
    >
      <Flag className={cn(iconSize, config.iconColor)} />
      {showLabel && config.label}
    </span>
  );
}
